import {
  type Locale,
  type OrderListItem,
  type PublicStage,
  type ShipmentResponse,
} from '@aps/contracts';
import { formatDate, formatEtaRange } from './format.js';

/**
 * Mijozga buyurtmani ko'rsatish — UCH QATLAM (docs/02-MAHSULOT.md).
 *
 *   1-qatlam (ro'yxat)   bitta sana = eng sekin qismning sanasi,
 *                        + "2 ta tovar ertaga tayyor". PROGRESS BAR YO'Q
 *   2-qatlam (buyurtma)  har jo'natma uchun sana va BIR SO'ZLI holat.
 *                        PROGRESS BAR YO'Q
 *   3-qatlam (jo'natma)  to'liq treker
 *
 * Treker — standart ko'rinish emas, CHUQURLASHUV. Foydalanuvchilarning
 * ko'pchiligi 3-qatlamga umuman kirmaydi.
 */

/** Mijozga ko'rinadigan bosqichlar. Ichki status HECH QACHON ko'rsatilmaydi. */
export const PUBLIC_STAGE_ORDER: readonly PublicStage[] = [
  'RECEIVED',
  'AT_SUPPLIER',
  'IN_TRANSIT',
  'AT_CUSTOMS',
  'PREPARING',
  'DELIVERING',
  'DELIVERED',
];

const STAGE_LABEL: Readonly<Record<Locale, Readonly<Record<PublicStage, string>>>> = {
  UZ: {
    RECEIVED: 'Qabul qilindi',
    AT_SUPPLIER: 'Ta’minotchida',
    IN_TRANSIT: 'Yo‘lda',
    AT_CUSTOMS: 'Bojxonada',
    PREPARING: 'Tayyorlanmoqda',
    DELIVERING: 'Yetkazilmoqda',
    DELIVERED: 'Yetkazildi',
    CANCELLED: 'Bekor qilindi',
  },
  RU: {
    RECEIVED: 'Принят',
    AT_SUPPLIER: 'У поставщика',
    IN_TRANSIT: 'В пути',
    AT_CUSTOMS: 'На таможне',
    PREPARING: 'Готовится',
    DELIVERING: 'Доставляется',
    DELIVERED: 'Доставлен',
    CANCELLED: 'Отменён',
  },
};

export function stageLabel(stage: PublicStage, locale: Locale): string {
  return STAGE_LABEL[locale][stage];
}

/**
 * Bosqichning treker ichidagi o'rni.
 *
 * `CANCELLED` trekerda ko'rsatilmaydi — u alohida holat, bosqich emas.
 */
export function stageIndex(stage: PublicStage): number {
  return PUBLIC_STAGE_ORDER.indexOf(stage);
}

export interface OrderSummary {
  /** Buyurtma yakunlanganmi. */
  done: boolean;
  /** Katta matn: sana yoki "Yetkazildi". */
  headline: string;
  /** Kichik matn: bir so'zli holat. */
  sub: string;
  /** Ijobiy qator: "2 ta tovar ertaga tayyor". Bo'lmasa `null`. */
  early: string | null;
}

/**
 * 1-qatlam: ro'yxatdagi bitta buyurtma.
 *
 * Bitta sana ko'rsatiladi — ENG SEKIN qismning sanasi. Erta tayyor
 * bo'ladigan qism alohida IJOBIY qator bo'lib chiqadi, chunki mijoz
 * uchun "bir narsani ertaga olaman" xabari qimmatli.
 */
export function orderListSummary(item: OrderListItem, locale: Locale): OrderSummary {
  const finished = item.publicStage === 'DELIVERED' || item.publicStage === 'CANCELLED';

  if (finished) {
    return {
      done: true,
      headline: stageLabel(item.publicStage, locale),
      sub: locale === 'RU' ? 'Завершён' : 'Yakunlandi',
      early: null,
    };
  }

  const date = formatDate(item.slowestEtaTo, locale);

  return {
    done: false,
    // Sana bo'lmasa holat matni ko'rsatiladi — bo'sh joy qolmaydi.
    headline: date === '' ? stageLabel(item.publicStage, locale) : date,
    sub: stageLabel(item.publicStage, locale),
    early: earlyLine(item, locale),
  };
}

function earlyLine(item: OrderListItem, locale: Locale): string | null {
  // Ijobiy qator faqat buyurtma BO'LINGAN bo'lsa ma'noga ega: bitta
  // jo'natmada "qismi ertaga tayyor" degan gap yo'q.
  if (item.shipmentCount < 2 || item.readySoonCount <= 0) return null;
  if (item.readySoonCount >= item.itemCount) return null;

  return locale === 'RU'
    ? `${String(item.readySoonCount)} товара будут готовы раньше`
    : `${String(item.readySoonCount)} ta tovar oldinroq tayyor bo‘ladi`;
}

export interface ShipmentSummary {
  /** Sana yoki oraliq. */
  eta: string;
  /** Bir so'zli holat — ichki status EMAS. */
  status: string;
  done: boolean;
}

/**
 * 2-qatlam: buyurtma ichidagi bitta jo'natma.
 *
 * Mijozga ICHKI status ko'rsatilmaydi: "CUSTOMS_DECLARED" unga hech
 * narsa demaydi. Faqat `publicStage`.
 */
export function shipmentSummary(
  shipment: Pick<ShipmentResponse, 'publicStage' | 'etaFrom' | 'etaTo'>,
  locale: Locale,
): ShipmentSummary {
  const done = shipment.publicStage === 'DELIVERED' || shipment.publicStage === 'CANCELLED';

  return {
    eta: done ? '' : formatEtaRange(shipment.etaFrom, shipment.etaTo, locale),
    status: stageLabel(shipment.publicStage, locale),
    done,
  };
}

export interface TrackerStep {
  stage: PublicStage;
  label: string;
  state: 'done' | 'current' | 'upcoming';
}

/**
 * 3-qatlam: to'liq treker bosqichlari.
 *
 * SHOP oqimida Xitoy bosqichlari (ta'minotchi, bojxona) umuman
 * ko'rsatilmaydi — mijoz o'zi bilan aloqasi yo'q bosqichni ko'rmasligi
 * kerak.
 */
export function trackerSteps(
  current: PublicStage,
  locale: Locale,
  options: { type: 'SHOP' | 'CHINA' },
): TrackerStep[] {
  const stages =
    options.type === 'SHOP'
      ? PUBLIC_STAGE_ORDER.filter(
          (stage) => stage !== 'AT_SUPPLIER' && stage !== 'IN_TRANSIT' && stage !== 'AT_CUSTOMS',
        )
      : PUBLIC_STAGE_ORDER;

  // Bekor qilingan jo'natmada treker ko'rsatilmaydi.
  if (current === 'CANCELLED') return [];

  const currentIndex = stages.indexOf(current);

  return stages.map((stage, index) => ({
    stage,
    label: stageLabel(stage, locale),
    state:
      currentIndex === -1
        ? 'upcoming'
        : index < currentIndex
          ? 'done'
          : index === currentIndex
            ? 'current'
            : 'upcoming',
  }));
}
